import type { Me } from '@ctcells/contract'
import { API_BASE_URL, useSessionStore } from '@ctcells/core'

import { createApiClient } from '../client.ts'
import { getAccessToken, setStoredOperations } from './tokenStore.ts'

/**
 * `GET /auth/me` — **나의 역할과 부를 수 있는 연산**(2026-09-26 실측 · admin 90 · operator 76 · viewer 23).
 *
 * 실물: `ctcells-core` `app/views/auth.py` `get_me` → `{ user_id, role, operations: string[] }`.
 * `operations` 의 원천은 계약 `x-roles` 다. 🔴 서버도 「**안내이지 권위가 아니다**」라 적는다 — 집행은 403.
 * ⚠ 실서버 `openapi.json` 의 200 스키마는 빈 object 다. 모양은 정본 `Me` 와 실측 원문이 기준이다.
 * ⚠ 이름(`username`·`name`)은 없다 — 사람 정보는 여전히 로그인 응답의 `user` 가 유일한 출처다.
 *
 * ## 왜 쿼리 캐시가 아니라 세션 스토어인가
 *
 * 이 값은 «서버 데이터»가 아니라 **세션의 속성**이다(역할과 같은 수명). 로그인 성공 때 `useLogin` 이
 * 쿼리 캐시를 **통째로 비우고**(`queryClient.clear()`) 세션 종료 때도 비운다 — 캐시에 두면 로그인 직후
 * 받은 목록이 그 청소에 같이 쓸린다. 그래서 역할이 사는 곳(세션 스토어 + `sessionStorage`)에 같이 둔다.
 *
 * ## 언제 부르나 — 폴링 없음
 *
 * - 로그인 성공 뒤 1회 · 부팅 복구(refresh 성공) 뒤 1회
 * - 갱신 응답의 세션 id 가 쥐고 있던 것과 달라졌을 때 1회. 같은 세션의 주기 갱신은 묻지 않는다
 * 🔴 **주기 조회를 하지 않는다**(DP-01). 역할이 서버에서 바뀌면 다음 로그인·새로고침에 반영된다
 *    — 그 사이 어긋남은 서버 403 이 막는다.
 *
 * ⚠ `clients.ts` 의 `orchestrator` 를 쓰지 않는다 — `authApi ↔ clients` 순환이 생긴다(`logout.ts` 머리말).
 *   이 요청은 로그인·갱신 **직후**에만 나가 토큰이 늘 새것이라 갱신 재시도가 필요 없다.
 */
const meClient = createApiClient({ baseUrl: API_BASE_URL, getAccessToken })

/** 응답 모양 검사. 🔴 모양이 틀리면 「모른다」(`null`)다 — 빈 목록으로 접으면 전 권한을 잃는다 */
function asMe(body: unknown): Me | null {
  if (typeof body !== 'object' || body === null) return null
  const record = body as Record<string, unknown>
  const { user_id, role, operations } = record
  if (typeof user_id !== 'string' || typeof role !== 'string') return null
  if (!Array.isArray(operations) || !operations.every((op) => typeof op === 'string')) return null
  return { user_id, role, operations: operations }
}

/**
 * `GET /auth/me` 1회. 실패(401 · 전송 오류 · 모양 불일치)는 전부 **`null` = 모른다**로 떨어뜨린다 —
 * 그때 화면은 역할 표(`ROLE_PERMISSIONS`)로 선다. 던지지 않는다.
 */
export async function fetchMe(): Promise<Me | null> {
  try {
    return asMe(await meClient.request<unknown>({ method: 'GET', path: '/auth/me' }))
  } catch {
    return null
  }
}

/** 진행 중인 조회(세션 id 별 single-flight) */
let inFlight: { sessionId: string | null; promise: Promise<void> } | null = null

/**
 * 지금 세션의 `operations` 를 받아 세션 스토어에 싣는다.
 *
 * 🔴 **응답이 도착했을 때 세션이 그대로인지 본다.** 그 사이 로그아웃·다른 계정 로그인이 있었으면 버린다
 *    — 남의 세션의 연산 목록으로 버튼을 여는 것은 사고다.
 * 🔴 실패하면 **쥐고 있던 목록을 지우지 않는다**(`null` 로 덮지 않는다) — 잠깐 끊긴 것으로 판정 원천을 잃지 않는다.
 */
export function loadOperations(): Promise<void> {
  const sessionId = useSessionStore.getState().sessionId
  if (inFlight !== null && inFlight.sessionId === sessionId) return inFlight.promise
  const promise = fetchMe()
    .then((me) => {
      const state = useSessionStore.getState()
      if (me === null || state.status !== 'authenticated' || state.sessionId !== sessionId) return
      setStoredOperations(me.operations)
      state.setOperations(me.operations)
    })
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null
    })
  inFlight = { sessionId, promise }
  return promise
}
